<script setup>
import { defineProps, defineEmits, computed, ref } from 'vue';
import CartaoTabelaBase from '@/components/v2/base/CartaoTabelaBase/CartaoTabelaBase.vue';
import BotaoIconeBase from '@/components/v2/base/BotaoIconeBase/BotaoIconeBase.vue';
import EntityService from '@/services/entityService';

const props = defineProps({
    itens: { type: Array, default: () => [] },
    historico: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    canAddItem: { type: Boolean, default: true },
    canEditItem: { type: Boolean, default: true },
    editOnlyStatusLC: { type: Boolean, default: false },
    editSemRestricaoStatus: { type: Boolean, default: false },
    editAllowedStatuses: { type: Array, default: () => [] },
});

const emits = defineEmits(['adicionar', 'editar', 'remover']);
const entityServiceHistorico = new EntityService('HistoricoOrdemDeServicoItens');

const headers = [
    { text: 'Sequência', value: 'sequencia', width: '110px' },
    { text: 'Item', value: 'itemCodigoExt', width: '140px' },
    { text: 'Status', value: 'status', width: '100px' },
    { text: 'Descrição', value: 'descricao', width: 'minmax(260px, 1fr)' },
    { text: 'Unidade', value: 'unidadeSigla', width: '100px' },
    { text: 'Quantidade', value: 'quantidade', width: '120px' },
    { text: 'Qtd p/ Embarcar', value: 'quantidadeParaEmbarcar', width: '140px' },
    { text: 'Qtd Embarcada', value: 'quantidadeEmbarcada', width: '140px' },
    { text: 'Qtd p/ Entregar', value: 'quantidadeParaEntregar', width: '140px' },
    { text: 'Qtd Entregue', value: 'quantidadeEntregue', width: '130px' },
    { text: 'Qtd Comprada', value: 'quantidadeCompra', width: '130px' },
    { text: 'Peso Total', value: 'pesoTotal', width: '110px' },
    { text: 'Valor Unitário', value: 'valorUnitario', width: '130px', type: 'money' },
    { text: 'Valor Total', value: 'valorTotal', width: '130px', type: 'money' },
    { text: 'Data Solicitação', value: 'dataSolicitacao', width: '140px', type: 'date' },
    { text: 'Data Necessidade', value: 'dataNecessidade', width: '140px', type: 'date' },
    { text: 'Data Entrega', value: 'dataEntrega', width: '130px', type: 'date' },
    { text: 'Desenho Fabricação', value: 'desenhoFabricacao', width: '180px' },
    { text: 'Desenho Montagem', value: 'desenhoMontagem', width: '180px' },
];

const dadosTabela = computed(() => (
    Array.isArray(props.itens)
        ? props.itens.map((item = {}, index) => ({
            id: item?.id || item?._id || `os-item-${index}`,
            sequencia: item?.sequencia || item?.sequence || '',
            itemCodigoExt: item?.itemCodigoExt || item?.itemCodigo || item?.codigoItem || '',
            status: item?.status || item?.situacao || '',
            descricao: item?.descricao || item?.description || '',
            unidadeSigla: item?.unidadeSigla || item?.siglaUnidade || '',
            quantidade: item?.quantidade ?? 0,
            quantidadeCompra: item?.quantidadeCompra ?? item?.qtdComprada ?? 0,
            quantidadeParaEntregar: item?.quantidadeParaEntregar ?? item?.qtdParaEntregar ?? 0,
            quantidadeEntregue: item?.quantidadeEntregue ?? item?.qtdEntregue ?? 0,
            quantidadeParaEmbarcar: item?.quantidadeParaEmbarcar ?? item?.qtdParaEmbarcar ?? 0,
            quantidadeEmbarcada: item?.quantidadeEmbarcada ?? item?.qtdEmbarcada ?? 0,
            pesoTotal: item?.pesoTotal ?? 0,
            valorUnitario: item?.valorUnitario ?? item?.valorUnit ?? item?.precoUnitario ?? 0,
            valorTotal: item?.valorTotal ?? item?.precoTotal ?? 0,
            dataSolicitacao: item?.dataSolicitacao || '',
            dataNecessidade: item?.dataNecessidade || '',
            dataEntrega: item?.dataEntrega || '',
            desenhoFabricacao: item?.desenhoFabricacao || '',
            desenhoMontagem: item?.desenhoMontagem || '',
            fornecedorDescricao: item?.fornecedorDescricao || item?.fornecedorNomeFantasia || item?.fornecedorNome || item?.fornecedor || '',
            raw: item,
        }))
        : []
));

const aoAdicionar = async () => {
    emits('adicionar');
};

const aoEditar = async (linha = {}) => {
    emits('editar', linha?.raw || {});
};

const aoRemover = async (linha = {}) => {
    emits('remover', linha?.raw || {});
};

const historicoDialog = ref(false);
const historicoLoading = ref(false);
const historicoItemSelecionado = ref(null);
const historicoItemLista = ref([]);

const pegaId = (value = null) => {
    if (value && typeof value === 'object') return value.id || value._id || '';
    return value || '';
};

const idsIguais = (a = '', b = '') => String(pegaId(a) || '').trim() === String(pegaId(b) || '').trim();

const normalizaTexto = (valor = '') => String(valor || '').trim().toUpperCase();

const extrairIds = (registro = {}) => {
    const candidatos = [
        registro?.id,
        registro?._id,
        registro?.valueId,
        registro?.ordemDeServicoItem,
        registro?.ordemDeServicoItens,
        registro?.ordemServicoItem,
        registro?.itemOrdemDeServico,
        registro?.itemOrdemDeServicoId,
        registro?.ordemDeServicoItem?.id,
        registro?.ordemDeServicoItem?._id,
    ];

    return [...new Set(
        candidatos
            .map((valor) => String(pegaId(valor) || '').trim())
            .filter(Boolean)
    )];
};

const historicoEhDoItem = (registro = {}, item = {}, linha = {}) => {
    const idsDoHistorico = extrairIds(registro);
    const idsDoItem = extrairIds({
        id: item?.id,
        _id: item?._id,
        ordemDeServicoItem: item?.ordemDeServicoItem,
        ordemDeServicoItens: item?.ordemDeServicoItens,
        itemOrdemDeServico: item?.itemOrdemDeServico,
    });

    const matchPorId = idsDoHistorico.some((idHistorico) => idsDoItem.some((idItem) => idsIguais(idHistorico, idItem)));
    if (matchPorId) return true;

    // Fallback para bases legadas onde o vínculo de ID não foi persistido no histórico.
    const sequenciaHistorico = normalizaTexto(registro?.sequencia || registro?.sequence || '');
    const sequenciaItem = normalizaTexto(linha?.sequencia || item?.sequencia || item?.sequence || '');

    const codigoHistorico = normalizaTexto(registro?.itemCodigoExt || registro?.itemCodigo || registro?.codigoItem || '');
    const codigoItem = normalizaTexto(linha?.itemCodigoExt || item?.itemCodigoExt || item?.itemCodigo || item?.codigoItem || '');

    const descricaoHistorico = normalizaTexto(registro?.descricao || registro?.description || '');
    const descricaoItem = normalizaTexto(linha?.descricao || item?.descricao || item?.description || '');

    if (sequenciaHistorico && sequenciaItem && sequenciaHistorico === sequenciaItem) {
        if (codigoHistorico && codigoItem && codigoHistorico === codigoItem) return true;
        if (descricaoHistorico && descricaoItem && descricaoHistorico === descricaoItem) return true;
    }

    return false;
};

const abrirHistoricoItem = async (linha = {}) => {
    const item = linha?.raw || {};
    const itemId = pegaId(item?.id || item?._id || linha?.id);

    historicoItemSelecionado.value = {
        descricao: linha?.descricao || item?.descricao || item?.description || '',
        sequencia: linha?.sequencia || item?.sequencia || item?.sequence || '',
        itemCodigoExt: linha?.itemCodigoExt || item?.itemCodigoExt || item?.itemCodigo || item?.codigoItem || '',
        unidadeSigla: linha?.unidadeSigla || item?.unidadeSigla || item?.siglaUnidade || '',
    };

    historicoDialog.value = true;
    historicoLoading.value = true;
    historicoItemLista.value = [];

    try {
        if (!itemId) {
            historicoItemLista.value = [];
            return;
        }

        const listaLocal = Array.isArray(props.historico) ? props.historico : [];
        const historicoLocalFiltrado = listaLocal
            .filter((registro = {}) => historicoEhDoItem(registro, item, linha))
            .sort((a, b) => {
                const dataA = new Date(a?.historicoDataCriacao || 0).getTime();
                const dataB = new Date(b?.historicoDataCriacao || 0).getTime();
                return dataB - dataA;
            });

        if (historicoLocalFiltrado.length > 0) {
            historicoItemLista.value = historicoLocalFiltrado;
            return;
        }

        // Tenta buscar o histórico do item com o campo correto
        const response = await entityServiceHistorico.getAll(
            1,
            1000,
            itemId,
            'ordemDeServicoItem', // Campo correto do relacionamento
            null,
            'Equals',
            null,
            'historicoDataCriacao'
        );

        const records = response?.data?.data?.data || response?.data?.data || [];

        historicoItemLista.value = (Array.isArray(records) ? records : [])
            .slice()
            .sort((a, b) => {
                const dataA = new Date(a?.historicoDataCriacao || 0).getTime();
                const dataB = new Date(b?.historicoDataCriacao || 0).getTime();
                return dataB - dataA;
            });

        if (historicoItemLista.value.length === 0) {
            historicoItemLista.value = listaLocal
                .filter((registro = {}) => historicoEhDoItem(registro, item, linha))
                .sort((a, b) => {
                    const dataA = new Date(a?.historicoDataCriacao || 0).getTime();
                    const dataB = new Date(b?.historicoDataCriacao || 0).getTime();
                    return dataB - dataA;
                });
        }
    } catch (erro) {
        console.error('Erro ao buscar histórico do item:', erro);
        // Em caso de erro, usa o histórico local
        const listaLocal = Array.isArray(props.historico) ? props.historico : [];
        historicoItemLista.value = listaLocal
            .filter((registro = {}) => historicoEhDoItem(registro, item, linha))
            .sort((a, b) => {
                const dataA = new Date(a?.historicoDataCriacao || 0).getTime();
                const dataB = new Date(b?.historicoDataCriacao || 0).getTime();
                return dataB - dataA;
            });
    } finally {
        historicoLoading.value = false;
    }
};

const formataDataBrasil = (valor = '') => {
    if (!valor) return '';

    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return String(valor);

    return new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(data);
};

const normalizaStatus = (status = '') => String(status || '').trim().toUpperCase();

const podeEditarLinha = (linha = {}) => {
    if (props.disabled || !props.canEditItem) return false;

    const status = normalizaStatus(linha?.status);
    const permitidos = Array.isArray(props.editAllowedStatuses)
        ? props.editAllowedStatuses.map((item) => normalizaStatus(item)).filter(Boolean)
        : [];

    if (permitidos.length > 0) return permitidos.includes(status);
    if (props.editSemRestricaoStatus) return true;
    if (props.editOnlyStatusLC) return status === 'LC';

    return status === 'ED';
};
</script>

<template>
    <div>
        <CartaoTabelaBase
            :headers="headers"
            :dados="dadosTabela"
            :disabled="disabled"
        >
        <template #header>
            <div class="d-flex align-center justify-space-between w-100">
                <span class="text-subtitle-1 font-weight-bold">Itens da OS</span>

                <BotaoIconeBase
                    v-if="!disabled && canAddItem"
                    icon="mdi-plus"
                    color="#3E737B"
                    text="Adicionar item"
                    :click="aoAdicionar"
                />
            </div>
        </template>

        <template #linha-botoes="{ dado: linha }">
            <div class="d-flex align-center">
                <BotaoIconeBase
                    icon="mdi-history"
                    color="#3E737B"
                    text="Histórico do item"
                    :click="() => abrirHistoricoItem(linha)"
                />
                <BotaoIconeBase
                    v-if="podeEditarLinha(linha)"
                    icon="mdi-pencil"
                    color="#3E737B"
                    text="Editar item"
                    :click="() => aoEditar(linha)"
                />
                <!-- Botão de remover item desabilitado para todos os usuários -->
            </div>
        </template>
        </CartaoTabelaBase>

        <v-dialog v-model="historicoDialog" max-width="900" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center justify-space-between">
                    <span class="text-subtitle-1 font-weight-bold">Histórico do Item</span>
                    <v-btn icon @click="historicoDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>

                <v-divider />

                <v-card-text class="pt-4">
                    <div v-if="historicoItemSelecionado" class="mb-4">
                        <div><strong>Sequência:</strong> {{ historicoItemSelecionado.sequencia || '-' }}</div>
                        <div><strong>Item:</strong> {{ historicoItemSelecionado.itemCodigoExt || '-' }}</div>
                        <div><strong>Descrição:</strong> {{ historicoItemSelecionado.descricao || '-' }}</div>
                        <div><strong>Unidade:</strong> {{ historicoItemSelecionado.unidadeSigla || '-' }}</div>
                    </div>

                    <v-data-table
                        :headers="[
                            { text: 'Data/Hora', value: 'historicoDataCriacao', sortable: false },
                            { text: 'Descrição', value: 'historicoDescricao', sortable: false },
                            { text: 'Status', value: 'status', sortable: false },
                            { text: 'Responsável', value: 'historicoResponsavelNome', sortable: false }
                        ]"
                        :items="historicoItemLista"
                        dense
                        hide-default-footer
                        class="elevation-0"
                        no-data-text="Sem histórico para este item"
                    >
                        <template #item.historicoDataCriacao="{ item }">
                            {{ formataDataBrasil(item?.historicoDataCriacao || '') || '-' }}
                        </template>

                        <template #item.historicoDescricao="{ item }">
                            {{ item?.historicoDescricao || '-' }}
                        </template>

                        <template #item.status="{ item }">
                            {{ item?.status || '-' }}
                        </template>

                        <template #item.historicoResponsavelNome="{ item }">
                            {{ item?.historicoResponsavelNome || item?.historicoResponsavel || '-' }}
                        </template>
                    </v-data-table>

                    <div v-if="historicoLoading" class="text-center py-3">
                        Carregando histórico...
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<style scoped>
.w-100 {
    width: 100%;
}
</style>
